import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  QueryList,
  AfterViewInit,
  NgZone,
} from '@angular/core';
import {GoogleMap, MapMarker} from '@angular/google-maps';

interface MarkerData {
  lat: number;
  lng: number;
  label?: string;
}

interface DisplayMarker {
  position: google.maps.LatLngLiteral;
  title: string;
  options: google.maps.MarkerOptions;
}

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() markers: MarkerData[] = [];
  // Optional externally provided current position
  @Input() currentPosition: {lat: number; lng: number} | null = null;
  // Auto-locate user via browser geolocation if currentPosition not supplied
  @Input() autoLocate = true;
  @ViewChild(GoogleMap) mapComponent: GoogleMap;
  @ViewChildren(MapMarker) markerComponents: QueryList<MapMarker>;

  height = '100%';
  width = '100%';
  zoom = 6;
  center: google.maps.LatLngLiteral = {lat: 16.463713, lng: 107.590866};
  options: google.maps.MapOptions = {
    fullscreenControl: false,
    mapTypeControl: false,
    streetViewControl: false,
    zoomControl: true,
  };

  displayMarkers: DisplayMarker[] = [];
  // For auto-shown tooltips we'll manage custom overlay labels per marker
  private labelOverlays: google.maps.OverlayView[] = [];

  ngOnInit() {
    this.syncMarkers();
    this.tryLocateUser();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['markers']) {
      this.syncMarkers();
    }
  }

  ngAfterViewInit() {
    this.markerComponents.changes.subscribe(() => this.renderAllLabels());
    Promise.resolve().then(() => this.renderAllLabels());
  }

  private syncMarkers() {
    const iconUrl = 'assets/images/logo-farm.png';
    this.displayMarkers = this.markers.map((m) => ({
      position: {lat: m.lat, lng: m.lng},
      title: m.label || '',
      options: this.buildMarkerOptions(iconUrl),
    }));
    // Inject current position marker (default red) at start if available
    if (this.currentPosition) {
      this.displayMarkers.unshift({
        position: {
          lat: this.currentPosition.lat,
          lng: this.currentPosition.lng,
        },
        title: 'Current Position',
        options: {optimized: true} as google.maps.MarkerOptions, // no custom icon -> default red pin
      });
    }
    // Optionally recenter if we have a current position
    if (this.currentPosition) {
      this.center = {...this.currentPosition} as any;
    }
  }

  private buildMarkerOptions(iconUrl: string): google.maps.MarkerOptions {
    // Guard if API not yet loaded: return basic options (icon URL only)
    if (!(window as any).google || !(window as any).google.maps) {
      return {
        icon: iconUrl,
        optimized: true,
      } as google.maps.MarkerOptions;
    }
    return {
      optimized: true,
      icon: {
        url: iconUrl,
        scaledSize: new google.maps.Size(40, 40), // adjust size as needed
        anchor: new google.maps.Point(20, 20), // center the icon (half of size)
      },
    } as google.maps.MarkerOptions;
  }

  private tryLocateUser() {
    if (
      this.currentPosition ||
      !this.autoLocate ||
      !('geolocation' in navigator)
    )
      return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.currentPosition = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        this.syncMarkers();
        this.renderAllLabels();
      },
      () => {
        /* silently ignore errors */
      },
      {enableHighAccuracy: true, timeout: 10000, maximumAge: 60000},
    );
  }

  markerClicked(marker: DisplayMarker) {
    /* click noop when all labels visible */
  }

  private clearLabelOverlays() {
    this.labelOverlays.forEach((o) => o.setMap(null));
    this.labelOverlays = [];
  }

  private renderAllLabels() {
    if (
      !(window as any).google ||
      !this.mapComponent ||
      !this.mapComponent.googleMap
    ) {
      return;
    }
    // Clear old overlays
    this.clearLabelOverlays();
    const map = this.mapComponent.googleMap!;
    this.displayMarkers.forEach((marker) => {
      const overlay = this.buildLabelOverlay(marker, map);
      overlay.setMap(map);
      this.labelOverlays.push(overlay);
    });
  }

  private buildLabelOverlay(
    marker: DisplayMarker,
    map: google.maps.Map,
  ): google.maps.OverlayView {
    const self = this;
    class LabelOverlay extends google.maps.OverlayView {
      div: HTMLDivElement;
      constructor() {
        super();
      }
      onAdd() {
        this.div = document.createElement('div');
        this.div.className = 'gmap-marker-label';
        this.div.style.position = 'absolute';
        this.div.style.top = '0';
        this.div.style.padding = '4px 8px';
        this.div.style.background = 'rgba(255,255,255,0.95)';
        this.div.style.border = '1px solid #e2e8f0';
        this.div.style.borderRadius = '4px';
        this.div.style.fontSize = '12px';
        this.div.style.lineHeight = '14px';
        this.div.style.whiteSpace = 'nowrap';
        this.div.style.boxShadow = '0 2px 4px rgba(0,0,0,0.15)';
        this.div.style.pointerEvents = 'none';
        this.div.innerText = marker.title || '';
        const panes = this.getPanes();
        panes?.floatPane.appendChild(this.div);
      }
      draw() {
        const projection = this.getProjection();
        if (!projection) return;
        const pos = projection.fromLatLngToDivPixel(
          new google.maps.LatLng(marker.position.lat, marker.position.lng),
        );
        if (pos && this.div) {
          this.div.style.left = pos.x + 'px';
          this.div.style.top = pos.y + 'px';
        }
      }
      onRemove() {
        this.div?.remove();
      }
    }
    return new LabelOverlay();
  }
}
